import Component from '@glimmer/component';
import { service } from '@ember/service';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Drawer from 'tio-reimagined/components/drawer';
import { formatCurrency } from 'tio-reimagined/format';
import { t } from 'ember-intl';
import {
  PhCalendarDots,
  PhSealCheck,
  PhClockCountdown,
  PhMoneyWavy,
  PhHourglassLow,
  PhCalendarX,
  PhLadderSimple,
} from 'ember-phosphor-icons';
import type PlanModel from 'tio-common/models/plan';
import type IntlService from 'ember-intl/services/intl';

/**
 * BenefitDetailDrawer — View Benefit plan details in a right drawer
 *
 * Displays plan name, description, and key benefit attributes
 * (annual limit, lifetime limit, payment cadence, contribution amount,
 * waiting period, milestone date, tiers).
 *
 * @example
 * ```gts
 * <BenefitDetailDrawer
 *   @isOpen={{this.showBenefitDrawer}}
 *   @onClose={{this.closeBenefitDrawer}}
 *   @plan={{this.plan}}
 * />
 * ```
 */
interface BenefitDetailDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** The employee's plan model */
    plan: PlanModel | null | undefined;
  };
  Element: HTMLDivElement;
}

export default class BenefitDetailDrawer extends Component<BenefitDetailDrawerSignature> {
  @service declare intl: IntlService;

  get plan() {
    return this.args.plan;
  }

  get planName() {
    return this.plan?.name || this.intl.t('accounts.benefit_detail.default_plan_name');
  }

  get planDescription() {
    return this.plan?.description || '';
  }

  get annualLimit() {
    const limit = this.plan?.annualLimit;
    if (!limit) return formatCurrency(0);
    return formatCurrency(limit / 100);
  }

  get lifetimeLimit() {
    const limit = this.plan?.lifetimeLimit;
    if (!limit) return formatCurrency(0);
    return formatCurrency(limit / 100);
  }

  get cadenceLabel() {
    const cadence = this.plan?.cadence;
    if (!cadence) return '—';
    switch (cadence.toUpperCase()) {
      case 'MONTHLY':
        return this.intl.t('accounts.benefit_detail.cadence_monthly');
      case 'QUARTERLY':
        return this.intl.t('accounts.benefit_detail.cadence_quarterly');
      case 'SEMI_ANNUALLY':
        return this.intl.t('accounts.benefit_detail.cadence_semi_annually');
      case 'ANNUALLY':
        return this.intl.t('accounts.benefit_detail.cadence_annually');
      case 'NEVER':
        return this.intl.t('accounts.benefit_detail.cadence_none');
      default:
        return cadence;
    }
  }

  get contributionAmount() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return formatCurrency(0);
    const amount = tiers[0]?.amount;
    if (!amount) return formatCurrency(0);
    return formatCurrency(amount / 100);
  }

  get waitingPeriodLabel() {
    const months = this.plan?.waitingPeriod;
    if (months == null || months === 0) return '—';
    if (months === 1) return this.intl.t('accounts.benefit_detail.employed_one_month');
    return this.intl.t('accounts.benefit_detail.employed_months', { months });
  }

  get milestoneDate() {
    const date = this.plan?.milestoneDate;
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = String(d.getFullYear()).slice(2);
    return `${month}/${day}/${year}`;
  }

  get tiersLabel() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return '—';
    if (tiers.length === 1) return this.intl.t('accounts.benefit_detail.cycle_one');
    return this.intl.t('accounts.benefit_detail.cycles_count', { count: tiers.length });
  }

  <template>
    <Drawer @isOpen={{@isOpen}} @onClose={{@onClose}} @size="lg" @placement="right" ...attributes>
      <:header>
        <div class="flex flex-col gap-2">
          <Heading @level={{3}} class="text-xl font-semibold text-gray-900">
            {{this.planName}}
          </Heading>
          {{#if this.planDescription}}
            <Text class="text-base text-gray-500 leading-relaxed">
              {{this.planDescription}}
            </Text>
          {{/if}}
        </div>
      </:header>
      <:body>
        <div class="flex flex-col gap-8 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhCalendarDots @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.annual_limit"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{this.annualLimit}}</span>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhSealCheck @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.lifetime_limit"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{this.lifetimeLimit}}</span>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhClockCountdown @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.payment_cadence"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{this.cadenceLabel}}</span>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhMoneyWavy @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.contribution_amount"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{this.contributionAmount}}</span>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhHourglassLow @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.waiting_period"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{this.waitingPeriodLabel}}</span>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhCalendarX @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.milestone_date"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{this.milestoneDate}}</span>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhLadderSimple @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.tiers_details"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{this.tiersLabel}}</span>
          </div>
        </div>
      </:body>
    </Drawer>
  </template>
}
